// Visual classification for the conversation thread rows shown in the review
// queue and Responses page.
//
// The trap this fixes: internal bookkeeping markers (a lead pushed to Potential
// Quotes, a "quote sent" event marker, a payment-link/handover marker) and
// UNSENT drafts all used to render as the same green "Michanic Response" bubble
// as a message the customer actually received. A consultant could not tell what
// was really sent. Each of these now gets its own muted style and an explicit
// "not sent to the customer" label.

// Response types that are internal markers / system events — NOT a chat message
// the customer received as this text.
const SYSTEM_MARKER_TYPES = new Set([
  'quote_lead_requested', // "Lead pushed to Potential Quotes"
  'quote_sent',           // "WhatsApp quote sent — ref …" (the customer got Brain's quote card, not this line)
  'payment_link_sent',    // payment/deposit link marker
  'whatsapp_handover',    // web-lead handover marker
]);

// A drafted reply awaiting human review — prepared, NOT sent.
const DRAFT_TYPES = new Set(['ai_draft']);

// Returns the visual kind for a conversation row:
//   'customer' | 'consultant' | 'note' | 'draft' | 'system' | 'ai'
export function conversationKind(message) {
  if (!message) return 'ai';
  // A quote decision. Sent and not-sent are deliberately different kinds:
  // "we quoted them" and "we couldn't, and here's why" are the two things a
  // consultant opening the thread most needs to tell apart at a glance.
  if (message.type === 'quote') {
    return message.quoteOutcome === 'sent' ? 'quote_sent' : 'quote_blocked';
  }
  if (message.type === 'customer' || message.type === 'consultant' || message.type === 'note') {
    return message.type;
  }
  const rt = (message.responseType || '').toLowerCase();
  if (DRAFT_TYPES.has(rt)) return 'draft';
  if (SYSTEM_MARKER_TYPES.has(rt)) return 'system';
  return 'ai'; // a genuine sent Michanic reply
}

// Tailwind classes + label per kind. `border`/`bubble`/`label` colour classes,
// `heading` is the row's uppercase label, `badge` (optional) is a short
// not-sent chip shown next to internal kinds.
export function conversationStyle(message) {
  const kind = conversationKind(message);
  switch (kind) {
    case 'customer':
      return { kind, border: 'border-blue-500', text: 'text-blue-600', bubble: 'bg-blue-50',
               heading: message.isParent ? 'Customer Message' : 'Customer Reply' };
    case 'consultant':
      return { kind, border: 'border-amber-500', text: 'text-amber-700', bubble: 'bg-amber-50',
               heading: 'Consultant Reply (sent directly via Freshdesk)' };
    case 'note':
      return { kind, border: 'border-purple-400', text: 'text-purple-700', bubble: 'bg-purple-50',
               heading: '🖼️ AI read attachment (internal)' };
    case 'draft':
      return { kind, border: 'border-orange-400 border-dashed', text: 'text-orange-700', bubble: 'bg-orange-50',
               heading: '📝 Draft — NOT sent (awaiting review)', badge: 'not sent' };
    case 'system':
      return { kind, border: 'border-slate-400 border-dashed', text: 'text-slate-600', bubble: 'bg-slate-100',
               heading: '⚙️ Internal marker — not a message to the customer', badge: 'internal' };
    case 'quote_sent':
      return { kind, border: 'border-emerald-500', text: 'text-emerald-700', bubble: 'bg-emerald-50',
               heading: '💰 Quote sent', badge: 'quote' };
    case 'quote_blocked':
      // Amber, not red: most of these are a guard working correctly (an open
      // quote on file, a consultant already replying), not a failure.
      return { kind, border: 'border-yellow-500 border-dashed', text: 'text-yellow-800', bubble: 'bg-yellow-50',
               heading: '⚠️ Quote NOT sent', badge: 'why' };
    default:
      return { kind, border: 'border-green-500', text: 'text-green-600', bubble: 'bg-green-50',
               heading: 'Michanic Response' };
  }
}
